import React, { useState } from 'react'
import { useList } from '@refinedev/core'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Link,
} from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import { format } from 'date-fns'
import { UUID } from 'crypto'

interface ChargePaymentRecord {
  id: number
  org_uuid: string
  platform: string
  amount: number
  type: 'Charge' | 'Payment'
  created_at: string
  external_reference?: string
}

interface OrgChargesPaymentsProps {
  orgUuid: UUID
}

export default function OrgChargesPayments({
  orgUuid,
}: OrgChargesPaymentsProps) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [typeFilter, setTypeFilter] = useState<'all' | 'Charge' | 'Payment'>(
    'all'
  )
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch paginated records for display (balance is calculated on backend)
  const { data, isLoading, isError, refetch } = useList<ChargePaymentRecord>({
    resource: 'charges-payments',
    pagination: {
      current: page + 1,
      pageSize: rowsPerPage,
    },
    filters: [], // Explicitly set empty filters to prevent automatic filter generation
    meta: {
      org_uuid: orgUuid,
      ...(typeFilter !== 'all' && { type_filter: typeFilter }),
      ...(searchTerm && { search: searchTerm.trim() }),
    },
    queryOptions: {
      enabled: !!orgUuid,
      queryKey: [
        'charges-payments',
        orgUuid,
        page + 1,
        rowsPerPage,
        typeFilter,
        searchTerm,
      ],
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  })

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
    }).format(amount)
  }

  const getTypeColor = (type: string) => {
    return type === 'Charge' ? 'error' : 'success'
  }

  const getExternalLink = (record: ChargePaymentRecord): string | null => {
    if (!record.external_reference) return null

    // Handle Stripe charges and payments
    if (record.platform === 'Stripe' && record.type === 'Charge') {
      // For Stripe charges, link to invoices
      return `https://dashboard.stripe.com/invoices/${record.external_reference}`
    } else if (record.platform === 'Stripe' && record.type === 'Payment') {
      // For Stripe payments, link to payments
      return `https://dashboard.stripe.com/payments/${record.external_reference}`
    }

    // Handle Xero invoices and payments
    if (
      record.type === 'Charge' &&
      (record.platform === 'Xero' || record.platform === 'Invoice')
    ) {
      // For invoices, use the correct Xero invoice URL format
      return `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${record.external_reference}`
    } else if (record.type === 'Payment' && record.platform === 'Xero') {
      // For payments, use the bank transaction URL format
      return `https://go.xero.com/Bank/ViewTransaction.aspx?bankTransactionID=${record.external_reference}`
    }

    return null
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box p={2}>
        <Typography color="error">
          Error loading charges and payments data
        </Typography>
      </Box>
    )
  }

  const records = data?.data || []
  const total = data?.total || 0

  const balanceOutstanding = (data as any)?.balance_outstanding || 0

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Charges & Payments History</Typography>

        {/* Balance Outstanding */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            border: 2,
            borderColor: balanceOutstanding > 0 ? 'error.main' : 'success.main',
            borderRadius: 2,
            textAlign: 'center',
            minWidth: 200,
          }}
        >
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Balance Outstanding
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={balanceOutstanding > 0 ? 'error.main' : 'success.main'}
          >
            {formatCurrency(balanceOutstanding)}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Charge">Charges</MenuItem>
            <MenuItem value="Payment">Payments</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Search"
          placeholder="Search by description or reference"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
        />
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No charges or payments found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>
                    {format(new Date(record.created_at), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={record.type}
                      size="small"
                      color={getTypeColor(record.type)}
                    />
                  </TableCell>
                  <TableCell>{record.platform || '-'}</TableCell>
                  <TableCell>
                    {getExternalLink(record) ? (
                      <Link
                        href={getExternalLink(record)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          textDecoration: 'none',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {record.external_reference}
                        <LaunchIcon fontSize="small" />
                      </Link>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {record.external_reference || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={
                        record.type === 'Charge' ? 'error.main' : 'success.main'
                      }
                      fontWeight="medium"
                    >
                      {formatCurrency(record.amount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Summary */}
      {records.length > 0 && (
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="body2" color="textSecondary">
            Showing {records.length} of {total} records
          </Typography>
        </Box>
      )}
    </Box>
  )
}
